import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import tz from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(tz);

// Default timezone for all bookings
export const DEFAULT_TIMEZONE = 'Europe/Budapest';

// Common format strings
export const DATE_FORMATS = {
  FULL: 'YYYY-MM-DD HH:mm',
  DATE_ONLY: 'YYYY-MM-DD',
  TIME_ONLY: 'HH:mm',
  EMAIL: 'DD MMM YYYY, HH:mm',
};

// =============================================================================
// CORE TIMEZONE FUNCTIONS
// =============================================================================

/**
 * Convert a local datetime to UTC for storage
 * @param {string|moment|dayjs} localDatetime - Local datetime (string, moment, or dayjs object)
 * @param {string} timezone - Timezone to interpret the local time in (defaults to Budapest)
 * @returns {string} UTC ISO string for storage
 */
export function localToUtc(localDatetime, timezone = DEFAULT_TIMEZONE) {
  if (!localDatetime) return null;
  let dateStr = localDatetime;
  if (typeof localDatetime === 'object' && localDatetime.format) {
    dateStr = localDatetime.format('YYYY-MM-DD HH:mm');
  }
  return dayjs.tz(dateStr, timezone).utc().toISOString();
}

/**
 * Convert a UTC datetime to local timezone for display
 * @param {string|Date} utcDatetime - UTC datetime from database
 * @param {string} timezone - Timezone to display in (defaults to Budapest)
 * @returns {dayjs.Dayjs} Dayjs object in the specified timezone
 */
export function utcToLocal(utcDatetime, timezone = DEFAULT_TIMEZONE) {
  if (!utcDatetime) return null;
  return dayjs.utc(utcDatetime).tz(timezone);
}

/**
 * Format a dayjs object to UTC ISO string for API filters
 * @param {dayjs.Dayjs} dayjsDate - A dayjs object
 * @returns {string} UTC ISO string
 */
export function toUtcIsoString(dayjsDate) {
  if (!dayjsDate) return null;
  return dayjsDate.utc().toISOString();
}

/**
 * Get a dayjs object for a booking datetime, handling both migrated and legacy bookings
 * @param {string|Date} datetime - The datetime from the booking
 * @param {string} storedTimezone - The timezone stored with the booking (if any)
 * @returns {dayjs.Dayjs} Dayjs object representing the local time
 */
export function getBookingDayjs(datetime, storedTimezone) {
  if (!datetime) return null;
  if (storedTimezone) {
    return dayjs.utc(datetime).tz(storedTimezone);
  }
  return dayjs.utc(datetime);
}

// =============================================================================
// FORMATTING FUNCTIONS
// =============================================================================

/**
 * Format a UTC datetime to Budapest timezone with specified format
 * @param {string|Date} dateTime - The date to format (in UTC)
 * @param {string} formatStr - Dayjs format string (default: 'YYYY-MM-DD HH:mm')
 * @returns {string} Formatted date string in Budapest timezone
 */
export function formatDateTime(dateTime, formatStr = DATE_FORMATS.FULL) {
  if (!dateTime) return '';
  return dayjs.utc(dateTime).tz(DEFAULT_TIMEZONE).format(formatStr);
}

/**
 * Format date only - converts UTC to Budapest timezone
 */
export function formatDate(dateTime) {
  return formatDateTime(dateTime, DATE_FORMATS.DATE_ONLY);
}

/**
 * Format time only - converts UTC to Budapest timezone
 */
export function formatTime(dateTime) {
  return formatDateTime(dateTime, DATE_FORMATS.TIME_ONLY);
}

/**
 * How long ago an instant was, in words — "just now", "6 min ago", "3 h ago".
 *
 * Hand-rolled rather than pulling in dayjs's relativeTime plugin for one
 * line, and deliberately INDEPENDENT of the viewer's timezone: it is a
 * difference of instants, so no conversion belongs in it. Everything else in
 * this file converts; this one subtracts.
 *
 * It lives here rather than beside its first caller because it now has two —
 * the booking modal's AI briefing and the auto-approval screen's "last
 * decision" line — and a second copy would be a second thing to get the
 * rounding wrong in. `now` is injectable so a test does not have to freeze
 * the clock.
 *
 * An unparseable input returns '' rather than a guess, so a caller renders
 * nothing instead of "NaN min ago".
 */
export function relativeAge(instant, now = Date.now()) {
  const then = Date.parse(instant);
  if (Number.isNaN(then)) return '';
  const seconds = Math.round((now - then) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} h ago`;
  const days = Math.floor(hours / 24);
  return `${days} d ago`;
}

// =============================================================================
// BOOKING-SPECIFIC FORMATTING (uses booking's timezone or default)
// =============================================================================

/**
 * Format a booking's datetime field with specified format
 * @param {Object} booking - The booking object
 * @param {string} field - The date field to format (default: 'date_datetime')
 * @param {string} formatStr - Dayjs format string (default: 'YYYY-MM-DD HH:mm')
 * @returns {string} Formatted date string in booking's timezone
 */
export function formatBookingField(booking, field = 'date_datetime', formatStr = DATE_FORMATS.FULL) {
  const date = booking[field];
  if (!date) return '';
  const bookingTz = booking.timezone || DEFAULT_TIMEZONE;
  return dayjs.utc(date).tz(bookingTz).format(formatStr);
}

/**
 * Format a booking datetime for display, handling both migrated and legacy bookings
 * @param {string|Date} datetime - The datetime from the booking
 * @param {string} storedTimezone - The timezone stored with the booking (if any)
 * @param {string} formatStr - Dayjs format string (default: 'YYYY-MM-DD HH:mm')
 * @returns {string} Formatted datetime string
 */
export function formatBookingDatetime(datetime, storedTimezone, formatStr = DATE_FORMATS.FULL) {
  if (!datetime) return '';
  if (storedTimezone) {
    return dayjs.utc(datetime).tz(storedTimezone).format(formatStr);
  }
  return dayjs.utc(datetime).format(formatStr);
}

// Convenience functions for common booking formats
export function formatBookingDate(booking, field = 'date_datetime') {
  return formatBookingField(booking, field, DATE_FORMATS.FULL);
}

export function formatBookingDateOnly(booking, field = 'date_datetime') {
  return formatBookingField(booking, field, DATE_FORMATS.DATE_ONLY);
}

export function formatBookingTimeOnly(booking, field = 'date_datetime') {
  return formatBookingField(booking, field, DATE_FORMATS.TIME_ONLY);
}

export function formatBookingDateEmail(booking, field = 'date_datetime') {
  return formatBookingField(booking, field, DATE_FORMATS.EMAIL);
}

export default {
  DEFAULT_TIMEZONE,
  DATE_FORMATS,
  localToUtc,
  utcToLocal,
  toUtcIsoString,
  getBookingDayjs,
  formatDateTime,
  formatDate,
  formatTime,
  relativeAge,
  formatBookingField,
  formatBookingDatetime,
  formatBookingDate,
  formatBookingDateOnly,
  formatBookingTimeOnly,
  formatBookingDateEmail,
};
